import React, { useState, useEffect, useContext } from 'react';
import ReactDOM from 'react-dom';
import { CHANNEL_NAME, CREATE_ENVIRONMENT_PERMISSION_MESSAGE, DELETE_CHANNEL_PERMISSION_MESSAGE, DELETE_ENVIRONMENT_PERMISSION_MESSAGE, NO_OF_ENVIRONMENT, UPDATE_CHANNEL_PERMISSION_MESSAGE, UPDATE_ENVIRONMENT_PERMISSION_MESSAGE } from '../Utils/Constants';
import { DeleteIcon, EditIcon, OverFlowIcon } from 'assests/GmsIcons';
import Table from 'Components/DesignSystem/Table/Table';
import { PAGE_GRID_MAX_HEIGHT } from 'Components/DesignSystem/Table/pageGridHeight';
import Menu from 'Components/DesignSystem/Menu/Menu';
import Button from 'Components/DesignSystem/Button/Button';
import Typography from 'Components/DesignSystem/Typography/Typography';
import { TableColumn } from 'types/DesignSystem/Table';
import { MenuItem } from 'types/DesignSystem/Menu';
import ConfirmDialog from '../Components/ConfirmDialog';
import { toast } from 'Components/DesignSystem/Toast/useToast';

import { AddEnvironmentDialog } from '../Environment/AddEnvironmentDialog';
import { CreateChannelDialog } from '../Channel/CreateChannelDialog';
import EnvironmentSidePanel from '../Environment/EnvironmentSidePanel';

import { TChannel, TEnvironment, TEnvironmentPayload, TUpdateEnvironmentPayload } from '../Types/channel.types';
import { TUpdateDomainPayload } from '../Types/domain.types';
import EnvironmentList from '../Environment/EnvironmentList';

import ServiceWrapper from 'Services/ServiceWrapper';
import AuthManager from 'Common/AuthManager';
import AppSettings from 'Settings/AppSettings';
import { AppContext } from 'Context/AppContext';
import { EnvironmentAPIResponse } from '../Types/env.types';

const api_base_url = AppSettings.GMS_BACKEND_API_URL;

/**
 * The client-side environment cap. Extracted from four bare `3`s here plus one in
 * `EnvironmentList`; the value and every gate are unchanged. **It is not enforced by
 * the backend** — see the component docblock.
 */
const MAX_ENVIRONMENTS = 3;

type ChannelTableProps = {
    partyId: string;
    domainId: string;
    searchTerm: string;
    showLoader: boolean;
    channels: TChannel[];
    setChannels: React.Dispatch<React.SetStateAction<TChannel[]>>;
    setShowLoader: (value: boolean) => void;
    setTotalRecords: (value: number) => void;
    paginationParams: { pageNumber: number; pageSize: number };
    onChannelDeleted?: () => void;
    refreshTrigger?: number;
    onChannelsLoaded?: () => void;
    readPermission: any
    createPermission: any
    updatePermission: any
    deletePermission: any
}

/**
 * The channel grid on the design-system `Table`, with expandable rows (§ 120).
 *
 * This is the table that needed a new DS capability. Every other list in the app is
 * flat; a channel row opens onto its environments, and `Table` had no expansion API at
 * all — the only other expandable tables in the codebase are seven hand-rolled
 * `<table>`s in CatalogManagement. Rather than add an eighth, `Table` gained
 * `renderExpandedRow` / `expandedRowKeys` / `onExpandChange` / `isRowExpandable`
 * (additive; the seven existing consumers pass nothing and are unchanged).
 *
 * Three things about this table are unlike the party and domain ones:
 *
 * - **Expansion is controlled and lazy.** `Table` holds no expansion state, which
 *   suits this screen: `toggleChannelExpansion` fetches the channel's environments on
 *   FIRST open and caches them in `channelEnvironments`. That fetch could not live
 *   inside the DS component.
 * - **A channel with no environments has nothing to open**, so `isRowExpandable`
 *   returns false and the row gets an empty cell rather than a chevron that does
 *   nothing (§ 69).
 * - **The environment-count cell is polymorphic**: the count, or an "Add Environment"
 *   button when the count is zero, plus "(Max limit reached)" at or above
 *   `MAX_ENVIRONMENTS`.
 *
 * ⚠ **`MAX_ENVIRONMENTS` is 3 and the backend does not enforce it.** Four sites here
 * gate on it and `EnvironmentList` hides its Add button above it, yet a channel in the
 * dev data holds nine environments — so the UI says "Max limit reached" about a limit
 * that is not real. The value and every gate are unchanged by this migration; whether
 * the cap or the copy is wrong is a product question.
 *
 * Still owed: this file is ~900 lines and owns channel CRUD, environment CRUD, the
 * side panel, three confirms and two dialogs. Splitting the environment half out is a
 * structural change, not a presentational one, and is deliberately not attempted here.
 */
const ChannelTable = ({ partyId, domainId, searchTerm, showLoader, channels, setChannels, setShowLoader, setTotalRecords, paginationParams, onChannelDeleted, refreshTrigger, onChannelsLoaded, readPermission, updatePermission, deletePermission, createPermission }: ChannelTableProps) => {
    const appContext = useContext(AppContext);
    const [openMenuFor, setOpenMenuFor] = useState<string | null>(null);
    const [menuPosition, setMenuPosition] = useState({ top: 0, right: 0 });
    const [expandedChannels, setExpandedChannels] = useState<Set<string>>(new Set());
    const [channelEnvironments, setChannelEnvironments] = useState<Record<string, TEnvironment[]>>({});
    const [loadingEnvironments, setLoadingEnvironments] = useState<Set<string>>(new Set());
    const [sortBy, setSortBy] = useState<string>('name');
    const [sortOrder, setSortOrder] = useState<string>('asc');
    const [deleteConfirmation, setDeleteConfirmation] = useState<{ isOpen: boolean; channelIndex: number | null; channelName: string; channelId: string | null; }>({ isOpen: false, channelIndex: null, channelName: '', channelId: null });
    const [isDeleting, setIsDeleting] = useState(false);
    const [addEnvironmentDialog, setAddEnvironmentDialog] = useState<{ isOpen: boolean; channelId: string | null; channelName: string; }>({ isOpen: false, channelId: null, channelName: '' });
    const [isCreatingEnvironment, setIsCreatingEnvironment] = useState(false);
    const [isDeletingEnvironment, setIsDeletingEnvironment] = useState(false);
    const [deleteEnvironmentConfirmation, setDeleteEnvironmentConfirmation] = useState<{
        isOpen: boolean;
        environment: TEnvironment | null;
    }>({
        isOpen: false,
        environment: null
    });
    const [editChannelDialog, setEditChannelDialog] = useState<{
        isOpen: boolean;
        channel: TChannel | null;
    }>({
        isOpen: false,
        channel: null
    });
    const [isUpdatingChannel, setIsUpdatingChannel] = useState(false);
    const [editEnvironmentDialog, setEditEnvironmentDialog] = useState<{
        isOpen: boolean;
        environment: TEnvironment | null;
    }>({
        isOpen: false,
        environment: null
    });
    const [isUpdatingEnvironment, setIsUpdatingEnvironment] = useState(false);
    const [productionChangeConfirmation, setProductionChangeConfirmation] = useState<{
        isOpen: boolean;
        formData: any;
        isCreating: boolean;
    }>({
        isOpen: false,
        formData: null,
        isCreating: true
    });
    const [environmentSidePanel, setEnvironmentSidePanel] = useState<{
        isOpen: boolean;
        environment: TEnvironment | null;
        channelId: string | null;
        mode: 'create' | 'edit';
    }>({
        isOpen: false,
        environment: null,
        channelId: null,
        mode: 'create'
    });

    useEffect(() => {
        if (partyId && domainId) {
            fetchChannels();
        }
    }, [partyId, domainId, paginationParams, searchTerm, sortBy, sortOrder, refreshTrigger]);

    const fetchChannels = async () => {
        setShowLoader(true);
        try {
            const channelResponse = await ServiceWrapper.doGet(
                `${api_base_url}party/${partyId}/industry/${domainId}/parent_channel?page=${paginationParams.pageNumber}&per_page=${paginationParams.pageSize}&sort_by=${sortBy}&sort_order=${sortOrder}&search_string=${searchTerm}`,
                null,
                null,
                AuthManager.getGmsToken()
            );
            const response = channelResponse?.data;
            if (response && response.data) {
                setChannels(response.data);
                setTotalRecords(response.pagination ? response.pagination.total : response.data.length);
            }
        } catch (error) {
            console.error('Error fetching channels:', error);
            toast.error('Failed to fetch channels. Please try again.');
        } finally {
            setShowLoader(false);
            if (onChannelsLoaded) {
                onChannelsLoaded();
            }
        }
    };

    const fetchEnvironments = async (channelId: string) => {
        setLoadingEnvironments(prev => new Set(Array.from(prev).concat(channelId)));
        try {
            const envResponse = await ServiceWrapper.doGet(`${api_base_url}parent_channel/${channelId}/channel`, null, null, AuthManager.getGmsToken(),);
            const response = envResponse?.data;
            if (response && response.data) {
                const environments: TEnvironment[] = response.data.map((env: any) => ({
                    id: env.id,
                    name: env.name,
                    key: env.key,
                    isProduction: env.is_prod,
                    channelId: channelId,
                    created_at: env.created_at,
                    updated_at: env.updated_at
                }));

                setChannelEnvironments(prev => ({
                    ...prev,
                    [channelId]: environments
                }));
            }
        } catch (error) {
            console.error('Error fetching environments:', error);
            toast.error('Failed to fetch environments. Please try again.');
        } finally {
            setLoadingEnvironments(prev => {
                const newSet = new Set(Array.from(prev));
                newSet.delete(channelId);
                return newSet;
            });
        }
    };

    const toggleChannelExpansion = async (channelId: string, hasEnvironments: boolean) => {
        const newExpanded = new Set(expandedChannels);
        if (newExpanded.has(channelId)) {
            newExpanded.delete(channelId);
        } else {
            newExpanded.add(channelId);
            if (hasEnvironments && !channelEnvironments[channelId]) {
                await fetchEnvironments(channelId);
            }
        }
        setExpandedChannels(newExpanded);
    };

    const handleColumnClick = (column: string) => {
        if (!showLoader) {
            if (sortBy === column) {
                setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
            } else {
                setSortBy(column);
                setSortOrder('asc');
            }
        }
    };

    const handleEdit = (channel: TChannel, index: number) => {
        if (!updatePermission) {
            toast.warning(UPDATE_CHANNEL_PERMISSION_MESSAGE);
            return;
        }
        setEditChannelDialog({
            isOpen: true,
            channel: channel
        });
    };

    const handleUpdateChannel = async (formData: any) => {
        const channel = editChannelDialog.channel;
        if (!channel) return;
        setIsUpdatingChannel(true);
        try {
            const payload: TUpdateDomainPayload = {
                name: formData.channelName
            };
            const channelResponse = await ServiceWrapper.doPut(`${api_base_url}parent_channel/${channel.id}`, payload, null, AuthManager.getGmsToken(),);
            const response = channelResponse?.data;
            if (response && response.done) {
                toast.success(response?.message ?? 'Channel updated successfully!');
                setEditChannelDialog({ isOpen: false, channel: null });
                await fetchChannels();
                await appContext.refreshHierarchy.refreshParties();
            } else {
                toast.error(response?.message || 'Failed to update channel');
            }
        } catch (error) {
            console.error('Error updating channel:', error);
            toast.error('An error occurred while updating the channel');
        } finally {
            setIsUpdatingChannel(false);
        }
    };

    const handleDeleteClick = (channel: TChannel, index: number) => {
        if (!deletePermission) {
            toast.warning(DELETE_CHANNEL_PERMISSION_MESSAGE);
            return;
        }
        setDeleteConfirmation({
            isOpen: true,
            channelIndex: index,
            channelName: channel.name,
            channelId: channel.id
        });
    };

    const handleDeleteConfirm = async () => {
        if (!deleteConfirmation.channelId) return;
        setIsDeleting(true);
        try {
            const deleteResponse = await ServiceWrapper.doDelete(`${api_base_url}parent_channel/${deleteConfirmation.channelId}`, null, null, AuthManager.getGmsToken(),);
            const response = deleteResponse?.data;
            if (response && response.done) {
                toast.success(response.message || 'Channel deleted successfully!');
                await fetchChannels();
                await appContext.refreshHierarchy.refreshParties();
            } else {
                toast.error(response?.message || 'Failed to delete channel');
                return;
            }
        } catch (error) {
            toast.error('Failed to delete channel. Please try again.');
        } finally {
            setIsDeleting(false);
            setDeleteConfirmation({
                isOpen: false,
                channelIndex: null,
                channelName: '',
                channelId: null
            });
        }
    };

    const handleDeleteCancel = () => {
        setDeleteConfirmation({
            isOpen: false,
            channelIndex: null,
            channelName: '',
            channelId: null
        });
    };

    const handleAddEnvironmentClick = (channel: TChannel) => {
        if (!createPermission) {
            toast.warning(CREATE_ENVIRONMENT_PERMISSION_MESSAGE);
            return;
        }
        if (channel.environment_count >= MAX_ENVIRONMENTS) {
            toast.error('Maximum 3 environments allowed per channel');
            return;
        }

        setEnvironmentSidePanel({
            isOpen: true,
            environment: null,
            channelId: channel.id,
            mode: 'create'
        });

        setAddEnvironmentDialog({
            isOpen: true,
            channelId: channel.id,
            channelName: channel.name
        });
    };

    const handleAddEnvironmentSave = async (formData: any) => {
        if (!addEnvironmentDialog.channelId || !partyId || !domainId) return;

        const channel = channels.find(ch => ch.id === addEnvironmentDialog.channelId);
        if (channel && channel.environment_count >= MAX_ENVIRONMENTS) {
            toast.error('Maximum 3 environments allowed per channel');
            return;
        }

        if (formData.useAsProduction && addEnvironmentDialog.channelId) {
            const environments = channelEnvironments[addEnvironmentDialog.channelId] || [];
            const hasProduction = environments.some(env => env.isProduction);

            if (hasProduction) {
                setProductionChangeConfirmation({
                    isOpen: true,
                    formData: formData,
                    isCreating: true
                });
                return;
            }
        }

        await createEnvironmentWithData(formData);
    };

    const createEnvironmentWithData = async (formData: any) => {
        setIsCreatingEnvironment(true);

        try {
            const payload: TEnvironmentPayload = {
                party_id: partyId,
                name: formData.environmentName,
                industry_id: domainId,
                is_prod: formData.useAsProduction || false,
                parent_channel_id: addEnvironmentDialog.channelId ?? ''
            };

            const envResponse = await ServiceWrapper.doPost(`${api_base_url}channel`, payload, null, AuthManager.getGmsToken(),);
            const response = envResponse?.data
            if (response && response.done) {
                toast.success('Environment added successfully!');
                setAddEnvironmentDialog({ isOpen: false, channelId: null, channelName: '' });

                if (expandedChannels.has(addEnvironmentDialog.channelId ?? '')) {
                    await fetchEnvironments(addEnvironmentDialog.channelId ?? '');
                }

                fetchChannels();
                await appContext.refreshHierarchy.refreshParties();
            } else {
                toast.error(response?.message || 'Failed to add environment');
            }
        } catch (error) {
            console.error('Error adding environment:', error);
            toast.error('An error occurred while adding the environment');
        } finally {
            setIsCreatingEnvironment(false);
        }
    };

    const handleEditEnvironment = (environment: TEnvironment) => {
        if (!updatePermission) {
            toast.warning(UPDATE_ENVIRONMENT_PERMISSION_MESSAGE);
            return;
        }
        setEnvironmentSidePanel({
            isOpen: true,
            environment: environment,
            channelId: environment.channelId,
            mode: 'edit'
        });
    };

    const handleUpdateEnvironment = async (formData: any) => {
        const environment = editEnvironmentDialog.environment;
        if (!environment) return;

        if (formData.useAsProduction && !environment.isProduction && environment.channelId) {
            const environments = channelEnvironments[environment.channelId] || [];
            const hasProduction = environments.some(env => env.isProduction && env.id !== environment.id);

            if (hasProduction) {
                setProductionChangeConfirmation({
                    isOpen: true,
                    formData: formData,
                    isCreating: false
                });
                return;
            }
        }

        await updateEnvironmentWithData(formData);
    };

    const updateEnvironmentWithData = async (formData: any) => {
        const environment = editEnvironmentDialog.environment;
        if (!environment) return;

        setIsUpdatingEnvironment(true);

        try {

            const payload: TUpdateEnvironmentPayload = {
                name: formData.environmentName,
                is_prod: formData.useAsProduction || false
            };

            const envResponse = await ServiceWrapper.doPost(`${api_base_url}channel/${environment.id}`, payload, null, AuthManager.getGmsToken(),);
            const response = envResponse?.data;

            if (response && response.done) {
                toast.success('Environment updated successfully!');
                setEditEnvironmentDialog({ isOpen: false, environment: null });

                if (environment.channelId && expandedChannels.has(environment.channelId)) {
                    await fetchEnvironments(environment.channelId);
                }

                fetchChannels();
                await appContext.refreshHierarchy.refreshParties();
            } else {
                toast.error(response?.message || 'Failed to update environment');
            }
        } catch (error) {
            console.error('Error updating environment:', error);
            toast.error('An error occurred while updating the environment');
        } finally {
            setIsUpdatingEnvironment(false);
        }
    };

    const handleProductionChangeConfirm = async () => {
        const { formData, isCreating } = productionChangeConfirmation;

        setProductionChangeConfirmation({
            isOpen: false,
            formData: null,
            isCreating: true
        });

        if (isCreating) {
            await createEnvironmentWithData(formData);
        } else {
            await updateEnvironmentWithData(formData);
        }
    };

    const handleProductionChangeCancel = () => {
        setProductionChangeConfirmation({
            isOpen: false,
            formData: null,
            isCreating: true
        });
    };

    const handleEnvironmentSidePanelSave = async (formData: EnvironmentAPIResponse) => {
        console.log("🚀 ~ handleEnvironmentSidePanelSave ~ formData:", formData)
        const { mode, environment, channelId } = environmentSidePanel;

        if (mode === 'create') {
            if (!channelId || !partyId || !domainId) return;

            const channel = channels.find(ch => ch.id === channelId);
            if (channel && channel.environment_count >= MAX_ENVIRONMENTS) {
                toast.error('Maximum 3 environments allowed per channel');
                return;
            }

            // Check for production conflict
            if (formData.basic_info.is_prod && channelId) {
                const environments = channelEnvironments[channelId] || [];
                const hasProduction = environments.some(env => env.isProduction);

                if (hasProduction) {
                    setProductionChangeConfirmation({
                        isOpen: true,
                        formData: formData,
                        isCreating: true
                    });
                    return;
                }
            }

            await createEnvironmentWithSidePanelData(formData, channelId);
        } else {
            // Edit mode
            if (!environment) return;

            if (formData.basic_info.is_prod && !environment.isProduction && environment.channelId) {
                const environments = channelEnvironments[environment.channelId] || [];
                const hasProduction = environments.some(env => env.isProduction && env.id !== environment.id);

                if (hasProduction) {
                    setProductionChangeConfirmation({
                        isOpen: true,
                        formData: formData,
                        isCreating: false
                    });
                    return;
                }
            }

            await updateEnvironmentWithSidePanelData(formData, environment);
        }
    };

    const createEnvironmentWithSidePanelData = async (formData: any, channelId: string) => {
        setIsCreatingEnvironment(true);

        try {
            const payload: TEnvironmentPayload = {
                party_id: partyId,
                name: formData.name,
                industry_id: domainId,
                is_prod: formData.isProduction || false,
                parent_channel_id: channelId
            };

            const envResponse = await ServiceWrapper.doPost(
                `${api_base_url}channel`,
                payload,
                null,
                AuthManager.getGmsToken()
            );
            const response = envResponse?.data;

            if (response && response.done) {
                toast.success('Environment added successfully!');
                setEnvironmentSidePanel({
                    isOpen: false,
                    environment: null,
                    channelId: null,
                    mode: 'create'
                });

                if (expandedChannels.has(channelId)) {
                    await fetchEnvironments(channelId);
                }

                fetchChannels();
                await appContext.refreshHierarchy.refreshParties();
            } else {
                toast.error(response?.message || 'Failed to add environment');
            }
        } catch (error) {
            console.error('Error adding environment:', error);
            toast.error('An error occurred while adding the environment');
        } finally {
            setIsCreatingEnvironment(false);
        }
    };

    const updateEnvironmentWithSidePanelData = async (payload: EnvironmentAPIResponse, environment: TEnvironment) => {
        setIsUpdatingEnvironment(true);


        try {
            const envResponse = await ServiceWrapper.doPost(
                `${api_base_url}channel/${environment.id}`,
                payload,
                null,
                AuthManager.getGmsToken()
            );
            const response = envResponse?.data;

            if (response && response.done) {
                toast.success('Environment updated successfully!');
                setEnvironmentSidePanel({
                    isOpen: false,
                    environment: null,
                    channelId: null,
                    mode: 'create'
                });

                if (environment.channelId && expandedChannels.has(environment.channelId)) {
                    await fetchEnvironments(environment.channelId);
                }

                fetchChannels();
                await appContext.refreshHierarchy.refreshParties();
            } else {
                toast.error(response?.message || 'Failed to update environment');
            }
        } catch (error) {
            console.error('Error updating environment:', error);
            toast.error('An error occurred while updating the environment');
        } finally {
            setIsUpdatingEnvironment(false);
        }
    };

    const handleDeleteEnvironmentClick = (environment: TEnvironment) => {
        if (!deletePermission) {
            toast.warning(DELETE_ENVIRONMENT_PERMISSION_MESSAGE);
            return;
        }
        setDeleteEnvironmentConfirmation({
            isOpen: true,
            environment: environment
        });
    };

    const handleDeleteEnvironmentConfirm = async () => {
        const environment = deleteEnvironmentConfirmation.environment;
        if (!environment) return;
        setIsDeletingEnvironment(true);
        try {
            const envResponse = await ServiceWrapper.doDelete(`${api_base_url}channel/${environment.id}`, null, null, AuthManager.getGmsToken(),);
            const response = envResponse?.data;
            if (response && response.done) {
                toast.success('Environment deleted successfully!');
                if (environment.channelId &&
                    expandedChannels.has(environment.channelId)) {
                    await fetchEnvironments(environment.channelId);
                }
                fetchChannels();
                await appContext.refreshHierarchy.refreshParties();
            } else {
                toast.error(response?.message || 'Failed to delete environment');
            }
        } catch (error) {
            console.error('Error deleting environment:', error);
            toast.error('Failed to delete environment. Please try again.');
        } finally {
            setIsDeletingEnvironment(false);
            setDeleteEnvironmentConfirmation({
                isOpen: false,
                environment: null
            });
        }
    };

    const handleDeleteEnvironmentCancel = () => {
        setDeleteEnvironmentConfirmation({
            isOpen: false,
            environment: null
        });
    };

    const getMenuItems = (channel: TChannel, index: number): MenuItem[] => [
        {
            id: `edit-${index}`,
            label: 'Edit',
            icon: <EditIcon />,
            'data-testid': `channel-table-menu-edit-${channel.id}`,
            onClick: () => { setOpenMenuFor(null); handleEdit(channel, index); },
        },
        /* A standalone divider ENTRY, not `divider: true` on Delete: the DS `Menu`
           renders the rule INSTEAD of the item it is flagged on, where the legacy
           `DropdownMenu` drew the rule and then the item. Ported verbatim, that flag
           deletes the Delete action with a clean typecheck (§ 118). */
        { id: `divider-before-delete-${index}`, divider: true },
        {
            id: `delete-${index}`,
            label: 'Delete',
            icon: <DeleteIcon />,
            'data-testid': `channel-table-menu-delete-${channel.id}`,
            onClick: () => { setOpenMenuFor(null); handleDeleteClick(channel, index); },
        },
    ];

    /* A fixed-position menu detaches from its row as soon as anything scrolls. */
    useEffect(() => {
        function handleScroll() {
            setOpenMenuFor(null);
        }
        window.addEventListener('scroll', handleScroll, true);
        return () => {
            window.removeEventListener('scroll', handleScroll, true);
        };
    }, []);

    const handleOverflowClick = (event: React.MouseEvent<HTMLButtonElement>, channelId: string) => {
        event.stopPropagation();
        const rect = event.currentTarget.getBoundingClientRect();
        setMenuPosition({ top: rect.bottom + 4, right: window.innerWidth - rect.right });
        setOpenMenuFor(prev => (prev === channelId ? null : channelId));
    };

    const columns: TableColumn<TChannel>[] = [
        {
            id: 'name',
            header: CHANNEL_NAME,
            accessor: 'name',
            width: '60%',
            sortable: true,
            render: (_value, channel, rowIndex) => (
                <span id={`channel-name-${rowIndex}`} data-testid={`channel-table-name-${channel.id}`}>
                    {channel.name}
                </span>
            ),
        },
        {
            id: 'environment_count',
            header: NO_OF_ENVIRONMENT,
            accessor: 'environment_count',
            width: '30%',
            sortable: true,
            render: (_value, channel, rowIndex) => (
                <div id={`no-of-environments-${rowIndex}`} className="tw-flex tw-items-center tw-gap-2">
                    {channel?.environment_count !== 0 ? (
                        <Typography variant="body-primary" as="span" data-testid={`channel-table-env-count-${channel.id}`}>
                            {channel.environment_count}
                        </Typography>
                    ) : (
                        /* The zero case is an ACTION, not a value — the only place on
                           this screen where a cell offers the next step. */
                        <Button
                            variant="primary"
                            size="sm"
                            onClick={() => handleAddEnvironmentClick(channel)}
                            data-testid={`channel-table-add-env-${channel.id}`}
                        >
                            Add Environment
                        </Button>
                    )}
                    {channel?.environment_count >= MAX_ENVIRONMENTS && (
                        <Typography
                            variant="caption-metadata"
                            as="span"
                            className="tw-text-ink-tertiary"
                            data-testid={`channel-table-max-limit-${channel.id}`}
                        >
                            (Max limit reached)
                        </Typography>
                    )}
                </div>
            ),
        },
        {
            id: 'actions',
            header: '',
            accessor: () => null,
            width: '10%',
            align: 'right',
            render: (_value, channel, rowIndex) => (
                <>
                    <button
                        type="button"
                        aria-label={`More options for ${channel.name}`}
                        aria-haspopup="menu"
                        aria-expanded={openMenuFor === channel.id}
                        /* Legacy `id` kept verbatim — specs key off `id` here as often
                           as `data-testid`. The testid moves to the channel's own id;
                           `channel-channeltable-button-2-${'{'}index{'}'}` renamed rows on
                           every sort. */
                        id={`overflow-btn-${rowIndex}`}
                        data-testid={`channel-table-overflow-${channel.id}`}
                        className="tw-cursor-pointer tw-rounded-ctl tw-p-1 hover:tw-bg-surface-hover-strong"
                        onClick={(e) => handleOverflowClick(e, channel.id)}
                    >
                        <OverFlowIcon />
                    </button>
                    {openMenuFor === channel.id ? ReactDOM.createPortal(
                        <div
                            className="tw-fixed tw-z-[9999] tw-w-48"
                            style={{ top: `${menuPosition.top}px`, right: `${menuPosition.right}px` }}
                        >
                            <Menu
                                variant="compact"
                                items={getMenuItems(channel, rowIndex)}
                                data-testid={`channel-table-menu-${channel.id}`}
                                onDismiss={() => setOpenMenuFor(null)}
                            />
                        </div>,
                        document.body
                    ) : null}
                </>
            ),
        },
    ];

    return (
        <>
            <div className="tw-mt-3">
                <Table<TChannel>
                    /* Bound the grid so <main> stops scrolling the page — see
                       `pageGridHeight.ts` for the measured reserve. `stickyHeader`
                       needs it: sticky resolves against a scrolling ancestor. */
                    stickyHeader
                    maxHeight={PAGE_GRID_MAX_HEIGHT}
                    columns={columns}
                    data={channels ?? []}
                    rowKey="id"
                    striped
                    loading={showLoader}
                    sortState={{ columnId: sortBy, direction: sortOrder === 'asc' ? 'asc' : 'desc' }}
                    onSort={(columnId) => handleColumnClick(columnId)}
                    emptyContent={
                        <Typography variant="body-primary" className="tw-text-ink-secondary">
                            No channels found
                        </Typography>
                    }
                    /* A channel with zero environments has nothing to open. */
                    isRowExpandable={(channel) => channel.environment_count > 0}
                    expandedRowKeys={Array.from(expandedChannels)}
                    onExpandChange={(rowKey) => {
                        const channel = channels.find(c => c.id === rowKey);
                        /* The toggle also FETCHES on first open, which is why expansion
                           has to be the caller's state. */
                        toggleChannelExpansion(String(rowKey), (channel?.environment_count ?? 0) > 0);
                    }}
                    expandLabel={(channel, expanded) =>
                        `${expanded ? 'Hide' : 'Show'} environments for ${channel.name}`
                    }
                    /*
                      The wait belongs on the CHEVRON, not inside the expanded row — and
                      this is the one place the two are not interchangeable.
                      `toggleChannelExpansion` awaits `fetchEnvironments` BEFORE calling
                      `setExpandedChannels`, so the row does not exist while the request
                      is in flight; by the time it renders, `loadingEnvironments` no
                      longer holds the id.

                      This was a real regression, caught in review: the legacy chevron
                      swapped to a spinner and went `disabled` during the fetch, and the
                      first version of this migration moved that feedback into
                      `renderExpandedRow` — where it could never render. The click had no
                      response at all, and a second click fired a second request.
                    */
                    isRowExpandLoading={(channel) => loadingEnvironments.has(channel.id)}
                    renderExpandedRow={(channel) => (
                        <EnvironmentList
                            environments={channelEnvironments[channel.id] || []}
                            /* Always false: the fetch is over before this row mounts.
                               `EnvironmentList` keeps the prop for its own tests and for
                               any future caller that expands first and fetches after. */
                            isLoading={false}
                            onAddEnvironment={() => handleAddEnvironmentClick(channel)}
                            onEditEnvironment={handleEditEnvironment}
                            onDeleteEnvironment={handleDeleteEnvironmentClick}
                        />
                    )}
                    id="channel-table"
                    data-testid="channel-table"
                />
            </div>

            <ConfirmDialog
                isOpen={deleteConfirmation.isOpen}
                title="Delete Channel"
                message={`Are you sure you want to delete the channel "${deleteConfirmation.channelName}"?`}
                secondaryMessage="This action cannot be undone."
                tone="danger"
                isLoading={isDeleting}
                onConfirm={handleDeleteConfirm}
                onCancel={handleDeleteCancel}
                data-testid="channel-delete-confirm"
            />

            <AddEnvironmentDialog
                isOpen={addEnvironmentDialog.isOpen}
                onClose={() => setAddEnvironmentDialog({ isOpen: false, channelId: null, channelName: '' })}
                onSave={handleAddEnvironmentSave}
                isLoading={isCreatingEnvironment}
                hasProductionEnv={addEnvironmentDialog.channelId ? (channelEnvironments[addEnvironmentDialog.channelId] || []).some(env => env.isProduction) : false}
            />

            <ConfirmDialog
                isOpen={deleteEnvironmentConfirmation.isOpen}
                title="Delete Environment"
                message={`Are you sure you want to delete the environment "${deleteEnvironmentConfirmation.environment?.name}"?`}
                secondaryMessage="This action cannot be undone."
                tone="danger"
                isLoading={isDeletingEnvironment}
                onConfirm={handleDeleteEnvironmentConfirm}
                onCancel={handleDeleteEnvironmentCancel}
                data-testid="environment-delete-confirm"
            />

            <CreateChannelDialog
                isOpen={editChannelDialog.isOpen}
                onClose={() => setEditChannelDialog({ isOpen: false, channel: null })}
                onSave={handleUpdateChannel}
                isLoading={isUpdatingChannel}
                editData={editChannelDialog.channel}
                mode="edit"
            />

            <AddEnvironmentDialog
                isOpen={editEnvironmentDialog.isOpen}
                onClose={() => setEditEnvironmentDialog({ isOpen: false, environment: null })}
                onSave={handleUpdateEnvironment}
                isLoading={isUpdatingEnvironment}
                editData={editEnvironmentDialog.environment}
                mode="edit"
                hasProductionEnv={editEnvironmentDialog.environment?.channelId ? (channelEnvironments[editEnvironmentDialog.environment.channelId] || []).some(env => env.isProduction) : false}
            />

            {/* NO `tone="danger"`. Changing which environment is production is a
                decision, not a deletion — the legacy dialog already used a blue
                confirm here (`confirmButtonClass` overriding its red default), and a
                red button on a reversible choice teaches people to ignore red. */}
            <ConfirmDialog
                isOpen={productionChangeConfirmation.isOpen}
                title="Change Production Environment"
                message="You are changing the production environment. Are you sure?"
                confirmText="Yes, Change"
                onConfirm={handleProductionChangeConfirm}
                onCancel={handleProductionChangeCancel}
                data-testid="production-change-confirm"
            />

            {environmentSidePanel.environment?.id && <EnvironmentSidePanel
                isOpen={environmentSidePanel.isOpen || false}
                onClose={() =>
                    setEnvironmentSidePanel({
                        isOpen: false,
                        environment: null,
                        channelId: null,
                        mode: 'create'
                    })
                }
                editData={environmentSidePanel.environment}
                onSave={handleEnvironmentSidePanelSave}
                isLoading={isCreatingEnvironment || isUpdatingEnvironment}
                selectedEnvironmentId={environmentSidePanel.environment?.id}
                selectedEnvironmentName={environmentSidePanel.environment?.name}
                allEnvironmentList={environmentSidePanel.environment?.channelId ? (channelEnvironments[environmentSidePanel.environment.channelId] || []).map(env => env.name) : []}
                hasProductionEnv={
                    environmentSidePanel.environment?.channelId
                        ? (channelEnvironments[environmentSidePanel.environment.channelId] || [])
                            .some(env => env.isProduction && env.id !== environmentSidePanel.environment?.id)
                        : false
                }
            />}
        </>
    );
}

export default ChannelTable;